import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import Checkmark from './components/Checkmark';

export default function FitnessLevel() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const [selectedLevel, setSelectedLevel] = useState<number>(3);

  const fitnessLevels = useMemo(
    () =>
      englishChoices
        ? englishChoices.map((choice: string, index: number) => ({
            en: choice,
            es: spanishChoices[index],
          }))
        : [],
    [englishChoices, spanishChoices]
  );

  const levelsWithLableComment: {
    en: { label: string; comment: string };
    es: { label: string; comment: string };
  }[] = useMemo(
    () =>
      fitnessLevels.map((fitnessLevel: { en: string; es: string }) => ({
        en: extractLabelAndComment(fitnessLevel.en),
        es: extractLabelAndComment(fitnessLevel.es),
      })),
    [fitnessLevels]
  );

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const labels = levelsWithLableComment.map((langLevel) =>
        langLevel?.[isSpanish ? 'es' : 'en']?.label.trim()
      );
      const userChoices =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let answer = '';
      try {
        const parsedChoices = JSON.parse(userChoices);
        answer = parsedChoices[0];
      } catch (error) {
        console.log(error);
      }
      const choiceLevel = answer?.split('-')[0]?.trim();
      const index = labels.indexOf(choiceLevel);
      setSelectedLevel(index >= 0 ? index + 1 : 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  const onContinue = () => {
    const fitnessItem = fitnessLevels[selectedLevel - 1];
    onSubmit({
      choice_value: JSON.stringify([fitnessItem.en]),
      spanish_choice_value: JSON.stringify([fitnessItem.es]),
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={2}
    >
      <div className='grid grid-cols-1 gap-4 lg:gap-6 md:grid-cols-2'>
        {levelsWithLableComment.map((level, index) => {
          const isSelected = index + 1 === selectedLevel;
          return (
            <motion.div
              key={level.en.label}
              onClick={() => setSelectedLevel(index + 1)}
              className={cn(
                'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76]',
                isSelected && 'border-primary text-primary bg-primary/10'
              )}
              whileTap={{ scale: 0.98 }}
            >
              <p className='text-sm font-medium leading-5'>
                <span className='font-semibold'>
                  {level?.[isSpanish ? 'es' : 'en'].label}
                </span>
                {' - '}
                {level?.[isSpanish ? 'es' : 'en'].comment}
              </p>

              <Checkmark isSelected={isSelected} />
            </motion.div>
          );
        })}
      </div>
    </QuestionnaireContainer>
  );
}
